import React, { useState, useRef, useEffect, memo } from 'react';
import { Icons } from '../Icons';
import { METRICS_CONFIG, METRIC_PLAYBOOKS } from '../../config/metrics';
import DeviceToggle from '../ui/DeviceToggle';
import SectionInfoPanel, { StatusSummary } from '../ui/SectionInfoPanel';
import { useLanguage } from '../../contexts/LanguageContext';
import { DiagnosticsPanel } from '../metrics/DiagnosticsPanel';
import { ComplexAuditInvitation } from '../metrics/QuickAuditModal';
import { shouldShowMetricCardSkeleton } from '../../utils/metricCardLoading';
import { buildDiagnosticCandidates } from '../../utils/metricDiagnosticCandidates';

const getMetricStatus = (metric, value) => {
  const thresholds = METRICS_CONFIG[metric];
  if (!thresholds || value === null || value === undefined) return 'unknown';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return 'unknown';
  if (numValue <= thresholds.good) return 'good';
  if (numValue <= thresholds.poor) return 'needs-improvement';
  return 'poor';
};

const formatValue = (metric, value) => {
  if (value === null || value === undefined) return '—';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '—';
  const config = METRICS_CONFIG[metric];
  if (config?.format) return config.format(numValue);
  if (metric === 'CLS') return numValue.toFixed(3);
  return `${Math.round(numValue)}${config?.unit || ''}`;
};

const PlaybookPanel = ({ metric, isOpen, onClose }) => {
  const { t } = useLanguage();
  const playbook = METRIC_PLAYBOOKS[metric];
  if (!playbook || !isOpen) return null;
  
  const metricKey = metric.toLowerCase();
  const title = t(`playbook.${metricKey}.title`);
  const why = t(`playbook.${metricKey}.why`);
  const checks = t(`playbook.${metricKey}.checks`);
  const fixes = t(`playbook.${metricKey}.fixes`);
  const owner = t(`playbook.${metricKey}.owner`);
  
  const effortColors = {
    'low': 'bg-green-500/20 text-green-400',
    'medium': 'bg-yellow-500/20 text-yellow-400',
    'high': 'bg-red-500/20 text-red-400'
  };
  
  return (
    <div className="mt-6 p-5 rounded-xl bg-slate-900 border-2 border-cyan-500/40 shadow-lg shadow-cyan-500/10 animate-slide-in">
      <div className="flex justify-between items-start mb-4">
        <h4 className="font-bold text-lg text-cyan-400">{title}</h4>
        <button 
          onClick={onClose}
          className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
        >
          <Icons.Close size={18} />
        </button>
      </div>
      
      <p className="text-sm text-slate-300 mb-4">{why}</p>
      
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
          <Icons.Eye size={12} /> {t('playbook.checkLabel')}
        </div>
        <ul className="space-y-1">
          {Array.isArray(checks) && checks.map((check, i) => (
            <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
              <span className="text-cyan-400 mt-1">•</span>
              {check}
            </li>
          ))}
        </ul>
      </div>
      
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wider text-cyan-500 mb-3 flex items-center gap-1 font-semibold">
          <Icons.Lightning size={14} /> {t('playbook.fixesLabel')}
        </div>
        <div className="space-y-3">
          {Array.isArray(fixes) && fixes.map((fix, i) => (
            <div key={i} className="p-4 rounded-lg bg-slate-800 border border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-white">{fix.action}</span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium text-center ${effortColors[fix.effort] || effortColors['medium']}`}>
                  {t(`playbook.effort.${fix.effort}`)}<br/>{t('playbook.effortLabel')}
                </span>
              </div>
              <p className="text-sm text-slate-300">{fix.detail}</p>
            </div>
          ))}
        </div>
      </div>
      
      <div className="text-sm text-slate-400 flex items-center gap-2 pt-2 border-t border-slate-700">
        <Icons.Trophy size={14} /> {t('playbook.ownerLabel')}: <span className="text-white font-medium">{owner}</span>
      </div>
    </div>
  );
};

// Map CrUX form factor to deep-analysis device name
const FORM_FACTOR_TO_DEEP = { PHONE: 'mobile', TABLET: 'tablet', DESKTOP: 'desktop' };

const extractP75 = (metricData) => {
  if (!metricData) return null;
  const v = metricData.value ?? metricData.p75 ?? metricData.percentiles?.p75 ?? null;
  const num = typeof v === 'string' ? parseFloat(v) : v;
  return (num === null || num === undefined || isNaN(num)) ? null : num;
};

// Pick the device the deep analysis should run on for a given metric.
// If the user selected a concrete device, respect it; on "All devices"
// pick the worst-performing device from CrUX data (mobile as fallback).
const pickDeepDevice = (metric, selectedDevice, byFormFactor) => {
  if (FORM_FACTOR_TO_DEEP[selectedDevice]) {
    return { device: FORM_FACTOR_TO_DEEP[selectedDevice], auto: false };
  }
  const rank = { poor: 2, 'needs-improvement': 1, good: 0, unknown: -1 };
  let best = null;
  for (const ff of ['PHONE', 'TABLET', 'DESKTOP']) {
    const md = byFormFactor?.[ff];
    if (!md || md.error) continue;
    const value = extractP75(md.metrics?.[metric]);
    if (value === null) continue;
    const status = getMetricStatus(metric, value);
    const cand = { ff, value, score: rank[status] ?? -1 };
    if (!best || cand.score > best.score || (cand.score === best.score && cand.value > best.value)) {
      best = cand;
    }
  }
  if (best) return { device: FORM_FACTOR_TO_DEEP[best.ff], auto: true, formFactor: best.ff };
  return { device: 'mobile', auto: true, formFactor: 'PHONE' };
};

const MetricCard = ({ metric, data, isLoading, url, strategy, user, onOpenAuth, onOpenPricing, deepDevice }) => {
  const { t, language } = useLanguage();
  const [showPlaybook, setShowPlaybook] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  const isLoggedIn = !!user;
  const tierSlug = typeof user?.tier === 'string'
    ? user.tier
    : (user?.tier?.slug || user?.tier?.name || null);
  const hasDeepAccess = user && (['pro', 'business', 'premium', 'enterprise'].includes(tierSlug) || user.is_admin);
  
  const handlePlaybookClick = () => {
    if (!isLoggedIn) {
      setShowRegisterModal(true);
      return;
    }
    setShowPlaybook(!showPlaybook);
    setShowDiagnostics(false);
  };
  
  const handleDiagnosticsClick = () => {
    if (!isLoggedIn) {
      setShowRegisterModal(true);
      return;
    }
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    setShowDiagnostics(!showDiagnostics);
    setShowPlaybook(false);
  };
  
  if (shouldShowMetricCardSkeleton(isLoading, data)) {
    return (
      <div className="glass-card metric-card p-6">
        <div className="skeleton h-6 w-20 rounded mb-4"></div>
        <div className="skeleton h-10 w-32 rounded mb-2"></div>
        <div className="skeleton h-4 w-full rounded"></div>
      </div>
    );
  }
  
  if (!data || data.value === null || data.value === undefined) return null;
  
  const value = data.value;
  const status = getMetricStatus(metric, value);
  const metricInfo = METRICS_CONFIG[metric];
  const distributions = data.distributions || [];
  const hasPlaybook = METRIC_PLAYBOOKS[metric];
  
  const distributionData = distributions.map((d, i) => ({
    name: [t('metricsDisplay.status.good'), t('metricsDisplay.status.needsImprovement'), t('metricsDisplay.status.poor')][i],
    value: (d.proportion || 0) * 100,
    color: ['#34d399', '#fbbf24', '#f87171'][i]
  }));
  
  const alertClass = status === 'needs-improvement' 
    ? 'metric-alert-border alert-fair' 
    : status === 'poor' 
      ? 'metric-alert-border alert-poor' 
      : '';
  
  return (
    <div className={`glass-card metric-card p-8 animate-slide-in flex flex-col ${alertClass}`}>
      <div className="flex justify-between items-start mb-5">
        <div>
          <div className="text-slate-400 text-sm uppercase tracking-wider mb-2 mono font-medium">{metric}</div>
          <div className="text-5xl font-bold mono mb-2">{formatValue(metric, value)}</div>
          <div className="text-slate-400 text-sm min-h-[40px] leading-relaxed">{t(`metricDescriptions.${metric.toLowerCase()}.short`)}</div>
        </div>
        <div className={`status-${status} text-xs font-semibold px-3 py-1.5 rounded-full uppercase tracking-wide`}>
          {status === 'good' ? (
            <span className="flex items-center gap-1"><Icons.Check size={14} /> {t('metricsDisplay.status.good')}</span>
          ) : status === 'needs-improvement' ? (
            <span className="flex items-center gap-1"><Icons.Warning size={14} /> {t('metricsDisplay.status.fair')}</span>
          ) : (
            <span className="flex items-center gap-1"><Icons.Error size={14} /> {t('metricsDisplay.status.poor')}</span>
          )}
        </div>
      </div>
      
      <div className="flex-grow">
        {distributionData.length > 0 && (
          <div className="mt-6">
            <div className="text-xs text-slate-500 mb-2">{t('metricsDisplay.userExperience')}</div>
            <div className="flex gap-1 h-3 rounded-full overflow-hidden mb-3">
              {distributionData.map((d, i) => (
                <div 
                  key={i}
                  style={{ 
                    width: `${d.value}%`,
                    backgroundColor: d.color,
                    transition: 'width 0.5s ease'
                  }}
                  title={`${d.name}: ${d.value.toFixed(1)}%`}
                />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {distributionData.map((d, i) => {
                const labels = [t('metricsDisplay.fast'), t('metricsDisplay.medium'), t('metricsDisplay.slow')];
                return (
                  <div key={i} className="flex items-center gap-1">
                    <div 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: d.color }}
                    />
                    <span className="text-slate-400">{d.value.toFixed(0)}% {labels[i]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      <div className="mt-4 min-h-[42px] space-y-2">
        {url && status !== 'good' && (
          <button
            onClick={handleDiagnosticsClick}
            className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              showDiagnostics
                ? 'bg-purple-500/20 border border-purple-500/40 text-purple-300'
                : 'bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 text-purple-400'
            }`}
          >
            <Icons.Search size={14} />
            {showDiagnostics 
              ? (language === 'cs' ? 'Skrýt příčiny' : 'Hide causes')
              : (language === 'cs' ? 'Zobrazit příčiny' : 'Show causes')}
            {!hasDeepAccess && <Icons.Lock size={12} className="ml-1 opacity-60" />}
          </button>
        )}
        {hasPlaybook && status !== 'good' && (
          <button
            onClick={handlePlaybookClick}
            className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              showPlaybook 
                ? 'bg-cyan-500/20 border border-cyan-500/40 text-cyan-400'
                : status === 'needs-improvement' 
                  ? 'bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400'
                  : 'bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400'
            }`}
          >
            <Icons.Info size={14} />
            {showPlaybook ? t('deviceMetrics.hide') : t('deviceMetrics.howToFix')}
            {!isLoggedIn && <Icons.Lock size={12} className="ml-1 opacity-60" />}
          </button>
        )}
      </div>
      
      {showDiagnostics && url && (
        <>
          {deepDevice?.auto && (
            <div className="mt-3 text-xs text-slate-400 flex items-center gap-1.5">
              <Icons.Info size={12} className="shrink-0" />
              {language === 'cs'
                ? `Hloubková analýza běží na zařízení: ${deepDevice.device === 'mobile' ? 'Mobil' : deepDevice.device === 'tablet' ? 'Tablet' : 'Desktop'} (nejhorší výsledek dle CrUX)`
                : `Deep analysis runs on: ${deepDevice.device} (worst device per CrUX)`}
            </div>
          )}
          <DiagnosticsPanel
            url={url}
            metric={metric}
            strategy={strategy}
            user={user}
            metricValue={data?.value}
            selectedDevice={deepDevice?.device || null}
            onClose={() => setShowDiagnostics(false)}
          />
        </>
      )}
      
      {showRegisterModal && (
        <div className="cv-app-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowRegisterModal(false)}>
          <div className="bg-slate-900 border-2 border-cyan-500/50 rounded-2xl p-6 max-w-md mx-4 shadow-2xl shadow-cyan-500/20" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                <Icons.User size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'cs' ? 'Registrace vyžadována' : 'Registration Required'}
              </h3>
              <p className="text-slate-400 mb-4">
                {language === 'cs' 
                  ? 'Pro zobrazení tipů jak opravit problémy se prosím zaregistrujte. Registrace je zdarma.'
                  : 'To view tips on how to fix issues, please register. Registration is free.'}
              </p>
              <div className="flex gap-3 justify-center">
                <button 
                  onClick={() => { setShowRegisterModal(false); onOpenAuth?.('register'); }}
                  className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
                >
                  {language === 'cs' ? 'Zaregistrovat se' : 'Register'}
                </button>
                <button 
                  onClick={() => { setShowRegisterModal(false); onOpenAuth?.('login'); }}
                  className="px-6 py-2.5 bg-slate-700 text-white font-semibold rounded-lg hover:bg-slate-600 transition-colors"
                >
                  {language === 'cs' ? 'Přihlásit se' : 'Login'}
                </button>
              </div>
              <button 
                onClick={() => setShowRegisterModal(false)}
                className="mt-4 text-sm text-slate-500 hover:text-slate-300 transition-colors"
              >
                {language === 'cs' ? 'Zavřít' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showUpgradeModal && (
        <div className="cv-app-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-slate-900 border-2 border-purple-500/50 rounded-2xl p-6 max-w-md mx-4 shadow-2xl shadow-purple-500/20" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Icons.Zap size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'cs' ? 'Hloubková analýza' : 'Deep Analysis'}
              </h3>
              <p className="text-slate-400 mb-4">
                {language === 'cs' 
                  ? 'Zobrazení příčin problémů je dostupné pouze pro tarify Premium a Enterprise.'
                  : 'Viewing root causes is available only for Premium and Enterprise plans.'}
              </p>
              <button 
                onClick={() => { setShowUpgradeModal(false); onOpenPricing && onOpenPricing(); }}
                className="inline-block px-6 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
              >
                {language === 'cs' ? 'Zobrazit tarify' : 'View Plans'}
              </button>
              <button 
                onClick={() => setShowUpgradeModal(false)}
                className="mt-4 block w-full text-sm text-slate-500 hover:text-slate-300 transition-colors"
              >
                {language === 'cs' ? 'Zavřít' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <PlaybookPanel 
        metric={metric} 
        isOpen={showPlaybook} 
        onClose={() => setShowPlaybook(false)} 
      />
    </div>
  );
};

export const CoreVitalsSection = memo(({ metrics, isLoading, onPlaybookClick, selectedDevice, onDeviceChange, showDeviceToggle = false, dataSource = 'crux', deviceStatuses = {}, url = null, user = null, onOpenAuth = null, onOpenPricing = null, failingVitals = [], byFormFactor = null, diagnosticCandidates = null }) => {
  const { t } = useLanguage();
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const coreMetrics = ['LCP', 'INP', 'CLS'];
  const isPageSpeedMode = dataSource === 'pagespeed' || dataSource === 'lighthouse';
  
  const getMetricData = (metricName) => {
    if (!metrics) return null;
    const metricData = metrics[metricName];
    if (!metricData) return null;
    
    // Support both direct values and CrUX API structure (p75 or percentiles.p75)
    let value = metricData.value;
    if (value === null || value === undefined) {
      value = metricData.p75 ?? metricData.percentiles?.p75 ?? null;
    }
    
    // Support both distributions and CrUX API structure (histogram)
    const distributions = metricData.distributions || metricData.histogram || [];
    
    return { value, distributions };
  };
  
  const currentDeviceStatus = deviceStatuses[selectedDevice] || deviceStatuses['ALL'] || 'good';
  
  return (
    <section id="core-vitals" className="mb-4">
      <div className="glass-card p-8 lg:p-10 rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center border border-cyan-500/30">
              <Icons.Chart size={22} className="text-cyan-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-white">Core Web Vitals</h2>
                <button
                  onClick={() => setShowInfoPanel(!showInfoPanel)}
                  className={`p-1.5 rounded-lg transition-colors ${showInfoPanel ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-cyan-400 hover:bg-slate-700/50'}`}
                  title={t('coreVitals.infoTooltip')}
                >
                  <Icons.Info size={16} />
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-1 max-w-full overflow-hidden">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30 shrink-0">
                  📈 {t('coreVitals.seoImpact')}
                </span>
                <p className="text-sm text-slate-400 hidden sm:block truncate max-w-[250px] lg:max-w-none">
                  {isPageSpeedMode ? t('coreVitals.pageSpeedDescription') : t('coreVitals.cruxDescription')}
                </p>
                <StatusSummary type="core" overallStatus={currentDeviceStatus} selectedDevice={selectedDevice} deviceStatuses={deviceStatuses} />
              </div>
            </div>
          </div>
          {showDeviceToggle && onDeviceChange && (
            <DeviceToggle 
              value={selectedDevice || (isPageSpeedMode ? 'PHONE' : 'ALL')} 
              onChange={onDeviceChange}
              showTablet={!isPageSpeedMode}
              pageSpeedMode={isPageSpeedMode}
              deviceStatuses={deviceStatuses}
            />
          )}
        </div>
        
        <SectionInfoPanel 
          type="core"
          isOpen={showInfoPanel}
          onClose={() => setShowInfoPanel(false)}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {coreMetrics.map((metric) => (
            <MetricCard
              key={metric}
              metric={metric}
              data={getMetricData(metric)}
              isLoading={isLoading}
              url={url}
              strategy={selectedDevice === 'PHONE' ? 'mobile' : 'desktop'}
              deepDevice={pickDeepDevice(metric, selectedDevice, byFormFactor)}
              user={user}
              onOpenAuth={onOpenAuth}
              onOpenPricing={onOpenPricing}
            />
          ))}
        </div>

        <ComplexAuditInvitation
          failingVitals={failingVitals}
          candidates={diagnosticCandidates || buildDiagnosticCandidates(byFormFactor)}
          url={url}
          user={user}
          onOpenAuth={onOpenAuth}
          onOpenPricing={onOpenPricing}
          isLoading={isLoading}
        />
      </div>
    </section>
  );
});

export default CoreVitalsSection;
